import type { Moment } from 'moment'
import moment from 'moment'
import type { FC } from 'react'
import { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'

import useUser from '@/hooks/useUser'
import { useGetSubjectsBySearch } from '@/lib/apollo'
import type { SubjectBySearchResult } from '@/types/Subject'
import { debounce } from '@/utils/debounce'

import ApplicantCard from './ApplicantCard'
import PaginatedList from './PaginatedList'
import SmallPill from './SmallPill'

type Props = {
  onApplicantSelected: (subjectId: string) => void
  onApplicantAuxSelected: (subjectId: string) => void
  searchTerm: string
  clearSearch: () => void
}

const Container = styled.div`
  display: grid;
  grid-template-rows: min-content 1fr;
  row-gap: 20px;
  min-height: 0;
`

const Controls = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

const SearchResults: FC<Props> = ({
  onApplicantSelected,
  onApplicantAuxSelected,
  searchTerm,
  clearSearch,
}) => {
  const { customerId } = useUser()
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>(searchTerm)
  const [limit, setLimit] = useState<number>(25)
  const [offset, setOffset] = useState<number>(0)
  const [lastUpdated, setLastUpdated] = useState<Moment>(moment())

  const { loading, error, data } = useGetSubjectsBySearch({
    variables: {
      customerId,
      query: debouncedSearchTerm,
      limit,
      offset,
    },
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })

  useEffect(() => {
    setOffset(0)
  }, [error])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSubmitSearch = useCallback(
    debounce((searchVal: string) => {
      setDebouncedSearchTerm(searchVal)
      setLastUpdated(moment())
    }, 300),
    [],
  )

  useEffect(() => {
    debouncedSubmitSearch(searchTerm)
  }, [searchTerm, debouncedSubmitSearch])

  return (
    <Container>
      <Controls>
        <SmallPill onClick={() => clearSearch()}>Exit Search</SmallPill>
      </Controls>
      <PaginatedList<SubjectBySearchResult>
        loading={loading}
        total={data?.searchForSubjects?.total ?? 0}
        lastUpdated={lastUpdated}
        error={error}
        limit={limit}
        onChangePage={(limit, offset) => {
          setLimit(limit)
          setOffset(offset)
        }}
        offset={offset}
        data={data?.searchForSubjects.results ?? []}
        row={subject => (
          <ApplicantCard
            onApplicantSelected={onApplicantSelected}
            onApplicantAuxSelected={onApplicantAuxSelected}
            applicant={subject}
          />
        )}
      />
    </Container>
  )
}

export default SearchResults
